import { useEffect, useState } from 'react';
import { apiGet, apiPatch, apiPost, API_BASE } from '~/lib/api';

/**
 * Settings → Booking — the engine switch (FareHarbor / native / Checkfront),
 * payments, policies, native schedule defaults, and the Checkfront hookup.
 * All values live in the D1 `settings` table; Worker secrets (Stripe keys,
 * Checkfront key pair) are surfaced read-only as ready/not-ready.
 */

interface SettingsResponse {
  settings: Record<string, string>;
  integrations: {
    stripe_ready: boolean;
    checkfront_keys_set: boolean;
    turnstile_ready: boolean;
  };
}

const MODE_HELP: Record<string, string> = {
  fareharbor:
    'Current live behavior — availability from the FareHarbor cache, checkout deep-links into FareHarbor (OTA distribution keeps working).',
  native:
    'Fully standalone — our calendar (capacity − bookings − blocked dates), our Stripe checkout. No per-booking platform fee.',
  checkfront:
    'Our checkout UI + Stripe, with every booking recorded into Checkfront so its calendar/ops stay the source of truth.',
};

export function BookingSettings() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [integrations, setIntegrations] = useState<SettingsResponse['integrations'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [cfTest, setCfTest] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);

  useEffect(() => {
    apiGet<SettingsResponse>('/api/admin/settings')
      .then((data) => {
        setValues(data.settings);
        setSaved(data.settings);
        setIntegrations(data.integrations);
        if (data.settings.checkfront_webhook_token) {
          setWebhookUrl(
            `${API_BASE}/api/webhooks/checkfront?token=${data.settings.checkfront_webhook_token}`,
          );
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: string, value: string) => setValues((v) => ({ ...v, [key]: value }));
  const dirty = Object.keys(values).some((k) => values[k] !== saved[k]);

  const startTimes: string[] = (() => {
    try {
      const v = JSON.parse(values.default_start_times ?? '[]');
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  })();
  const setStartTimes = (times: string[]) => set('default_start_times', JSON.stringify(times));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const changed: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== saved[k]) changed[k] = v;
      }
      const res = await apiPatch<SettingsResponse>('/api/admin/settings', { values: changed });
      setValues(res.settings);
      setSaved(res.settings);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const testCheckfront = async () => {
    setCfTest('Testing…');
    try {
      const r = await apiPost<{ ok: boolean; item_count?: number; error?: string }>(
        '/api/admin/settings/checkfront/test',
        {},
      );
      setCfTest(r.ok ? `Connected — ${r.item_count} items visible.` : `Failed: ${r.error}`);
    } catch (e) {
      setCfTest(`Failed: ${(e as Error).message}`);
    }
  };

  const generateWebhookToken = async () => {
    if (webhookUrl && !window.confirm('Rotate the webhook token? The old URL stops working.')) return;
    try {
      const r = await apiPost<{ token: string; webhook_url: string }>(
        '/api/admin/settings/checkfront/webhook-token',
        {},
      );
      setWebhookUrl(r.webhook_url);
      setValues((v) => ({ ...v, checkfront_webhook_token: r.token }));
      setSaved((v) => ({ ...v, checkfront_webhook_token: r.token }));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (loading) return <div className="trc-page"><p>Loading…</p></div>;

  const mode = values.booking_mode ?? 'fareharbor';

  return (
    <div className="trc-page">
      <header className="trc-page-header">
        <p className="trc-eyebrow">Admin</p>
        <h1 className="trc-osw trc-page-title">Booking Settings</h1>
      </header>
      {error && <p className="trc-error">Error: {error}</p>}

      <div className="trc-form">
        <fieldset className="trc-fieldset">
          <legend>Booking engine</legend>
          <p className="trc-fieldset-help">
            Site-wide switch — every trip without a per-trip override follows this. Changing it
            affects every Book button on the site.
          </p>
          <div className="trc-field">
            <label>Engine</label>
            <select value={mode} onChange={(e) => set('booking_mode', e.target.value)}>
              <option value="fareharbor">FareHarbor (deep links)</option>
              <option value="native">Native — runs fully on its own</option>
              <option value="checkfront">Checkfront-connected</option>
            </select>
            <p className="trc-fieldset-help">{MODE_HELP[mode]}</p>
          </div>
          <div className="trc-field">
            <label>External booking URL override</label>
            <input
              value={values.booking_url ?? ''}
              placeholder="https:// — leave empty to use the engine above"
              onChange={(e) => set('booking_url', e.target.value)}
            />
            <p className="trc-fieldset-help">
              SITE-WIDE escape hatch: when set, every Book button links straight here instead.
              Needs a site Publish to go live (links are baked at build time).
            </p>
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Payments</legend>
          <p className="trc-fieldset-help">
            Stripe: <strong>{integrations?.stripe_ready ? 'connected' : 'not configured'}</strong>
            {!integrations?.stripe_ready &&
              ' — until keys are set, native/Checkfront checkout takes booking requests without charging.'}
          </p>
          <div className="trc-field-row">
            <div className="trc-field">
              <label>Deposit mode</label>
              <select
                value={values.deposit_required === '1' ? '1' : '0'}
                onChange={(e) => set('deposit_required', e.target.value)}
              >
                <option value="0">Charge full amount at booking</option>
                <option value="1">Deposit now, balance auto-charged later</option>
              </select>
            </div>
            <div className="trc-field">
              <label>Deposit %</label>
              <input
                type="number"
                min={1}
                max={100}
                value={values.deposit_percent ?? '25'}
                onChange={(e) => set('deposit_percent', e.target.value)}
              />
            </div>
            <div className="trc-field">
              <label>Balance charged (hours before trip)</label>
              <input
                type="number"
                min={0}
                value={values.balance_due_hours_before ?? '24'}
                onChange={(e) => set('balance_due_hours_before', e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Cancellation policy</legend>
          <div className="trc-field">
            <label>Policy text (shown at checkout + in confirmation emails)</label>
            <textarea
              rows={3}
              value={values.cancellation_policy ?? ''}
              onChange={(e) => set('cancellation_policy', e.target.value)}
            />
          </div>
          <div className="trc-field-row">
            <div className="trc-field">
              <label>Free cancellation window (hours)</label>
              <input
                type="number"
                min={0}
                value={values.cancellation_hours ?? '72'}
                onChange={(e) => set('cancellation_hours', e.target.value)}
              />
            </div>
            <div className="trc-field">
              <label>Refund % inside the window</label>
              <input
                type="number"
                min={0}
                max={100}
                value={values.cancellation_refund_pct ?? '100'}
                onChange={(e) => set('cancellation_refund_pct', e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Native schedule rules</legend>
          <p className="trc-fieldset-help">
            Apply when a trip's availability comes from our own inventory. Per-trip departure
            times override the defaults below.
          </p>
          <div className="trc-field-row">
            <div className="trc-field">
              <label>Lead time (hours notice)</label>
              <input
                type="number"
                min={0}
                value={values.tour_lead_time_hours ?? '0'}
                onChange={(e) => set('tour_lead_time_hours', e.target.value)}
              />
            </div>
            <div className="trc-field">
              <label>Same-day cutoff (minutes before start)</label>
              <input
                type="number"
                value={values.default_cutoff_minutes ?? '0'}
                onChange={(e) => set('default_cutoff_minutes', e.target.value)}
              />
            </div>
            <div className="trc-field">
              <label>Booking window (days ahead)</label>
              <input
                type="number"
                min={1}
                value={values.max_advance_days ?? '365'}
                onChange={(e) => set('max_advance_days', e.target.value)}
              />
            </div>
          </div>
          <div className="trc-field">
            <label>Default departure times</label>
            {startTimes.length === 0 && (
              <p className="trc-fieldset-help">
                None — trips book day-level (one capacity pool per day) unless the trip sets its
                own departure times.
              </p>
            )}
            {startTimes.map((t, i) => (
              <div key={i} className="trc-list-item">
                <input
                  type="time"
                  value={t}
                  onChange={(e) => {
                    const next = [...startTimes];
                    next[i] = e.target.value;
                    setStartTimes(next);
                  }}
                />
                <button
                  type="button"
                  className="trc-btn trc-btn--ghost"
                  onClick={() => setStartTimes(startTimes.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="trc-btn trc-btn--ghost"
              onClick={() => setStartTimes([...startTimes, '07:00'])}
            >
              + Add departure time
            </button>
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Checkfront connection</legend>
          <p className="trc-fieldset-help">
            API keys: <strong>{integrations?.checkfront_keys_set ? 'set' : 'not set'}</strong>
            {!integrations?.checkfront_keys_set &&
              ' — Matt adds CHECKFRONT_API_KEY / CHECKFRONT_API_SECRET as Worker secrets.'}
          </p>
          <div className="trc-field">
            <label>Account subdomain</label>
            <input
              value={values.checkfront_account ?? ''}
              placeholder="e.g. therivercrew (from therivercrew.checkfront.com)"
              onChange={(e) => set('checkfront_account', e.target.value)}
            />
          </div>
          <div className="trc-form-actions">
            <button
              type="button"
              className="trc-btn"
              disabled={values.checkfront_account !== saved.checkfront_account}
              title={
                values.checkfront_account !== saved.checkfront_account
                  ? 'Save settings first — the test probes the saved subdomain'
                  : undefined
              }
              onClick={testCheckfront}
            >
              Test connection
            </button>
            {values.checkfront_account !== saved.checkfront_account && (
              <span className="trc-fieldset-help">Save first — the test uses the saved subdomain.</span>
            )}
            {cfTest && <span className="trc-fieldset-help">{cfTest}</span>}
          </div>
          <div className="trc-field">
            <label>Webhook</label>
            {webhookUrl ? (
              <p className="trc-fieldset-help">
                Paste into Checkfront → Manage → Developer → Webhooks (Booking Status Change +
                Item Update):
                <br />
                <code>{webhookUrl}</code>
              </p>
            ) : (
              <p className="trc-fieldset-help">No webhook token yet.</p>
            )}
            <button type="button" className="trc-btn trc-btn--ghost" onClick={generateWebhookToken}>
              {webhookUrl ? 'Rotate token' : 'Generate webhook URL'}
            </button>
          </div>
        </fieldset>

        <div className="trc-form-actions">
          <button className="trc-btn" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {savedFlash && <span className="trc-fieldset-help">Saved.</span>}
        </div>
      </div>
    </div>
  );
}
